import axios, {parseAxiosPromise} from '@/axiosInstance'
import {z} from 'zod'
import {MailBatchSummary, MailLog, MailLogStatus, PreviewRecipient, SendMailPayload} from '@/features/mailer/types'
import {httpGetPatients} from '@/features/patients/services/patients.http'
import {httpGetUsers, HttpInfiniteUsersResponse} from '@/features/users/http/users.http'
import {PatientSmall} from '@/features/patients/types'
import {PaginationResponse} from '@/types'
import {captureException} from '@sentry/react'

// The mailer batches endpoints are inconsistent: the list endpoint returns pagination
// metadata in snake_case (per_page, current_page, last_page) while the detail endpoint
// uses camelCase (perPage, currentPage, lastPage). Normalize both shapes upfront so the
// rest of the app keeps working with the project-wide camelCase `PaginationResponse`.
const MailerPaginationMeta = z.preprocess(
    val => {
        if (typeof val !== 'object' || val === null) return val
        const v = val as Record<string, unknown>
        return {
            total: v.total,
            perPage: v.perPage ?? v.per_page,
            currentPage: v.currentPage ?? v.current_page,
            lastPage: v.lastPage ?? v.last_page
        }
    },
    z.object({
        total: z.number(),
        perPage: z.number(),
        currentPage: z.number(),
        lastPage: z.number()
    })
)

export const MailBatchListResponse = z.object({
    data: z.array(MailBatchSummary),
    meta: MailerPaginationMeta
})
export type MailBatchListResponse = z.infer<typeof MailBatchListResponse>

export const MailBatchDetailResponse = z.object({
    data: z.array(MailLog),
    meta: MailerPaginationMeta
})
export type MailBatchDetailResponse = z.infer<typeof MailBatchDetailResponse>

export interface HttpGetMailBatchesParams {
    page: number
    limit: number
    search?: string
}
export const httpGetMailBatches = (params: HttpGetMailBatchesParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('/mailer/batches', {params}),
        responseShape: MailBatchListResponse,
        onZodError: captureException
    })

export interface HttpGetMailBatchDetailParams {
    batchId: string
    page: number
    limit: number
    status?: MailLogStatus
}
export const httpGetMailBatchDetail = ({batchId, ...params}: HttpGetMailBatchDetailParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get(`/mailer/batches/${batchId}`, {params}),
        responseShape: MailBatchDetailResponse,
        onZodError: captureException
    })

const PatientPreviewResponse = z.object({
    data: z.array(PatientSmall),
    meta: PaginationResponse
})

// Send email
export interface HttpSendMailOptions {
    payload: SendMailPayload
    attachments?: File[]
}
export const httpSendMail = ({payload, attachments}: HttpSendMailOptions) => {
    const formData = new FormData()
    formData.append('subject', payload.subject)
    formData.append('body', payload.body)
    formData.append('recipients', JSON.stringify(payload.recipients))

    if (attachments?.length) {
        attachments.forEach(file => {
            formData.append('attachments', file)
        })
    }

    return axios.post('/mailer/send', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    })
}

// Preview recipients
export const PAGE_LIMIT = 50

type PreviewMeta = {total: number; currentPage: number; lastPage: number}
type PreviewPage = {data: PreviewRecipient[]; meta: PreviewMeta}

export const fetchPatientsPreview = async (
    recipients: SendMailPayload['recipients'],
    page: number
): Promise<PreviewPage> => {
    if (!recipients.patients) return {data: [], meta: {total: 0, currentPage: 1, lastPage: 1}}
    const res = await parseAxiosPromise({
        axiosPromise: httpGetPatients({
            params: {page, limit: PAGE_LIMIT},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.patients.filters as any}
        }),
        responseShape: PatientPreviewResponse,
        onZodError: captureException
    })
    return {
        data: res.data.map(p => ({
            id: p.id,
            fullName: p.fullName,
            email: p.contacts.find(c => c.type === 'email')?.contact ?? ''
        })),
        meta: {
            total: res.meta.total,
            currentPage: res.meta.currentPage,
            lastPage: res.meta.lastPage
        }
    }
}

export const fetchPatientsCount = async (recipients: SendMailPayload['recipients']): Promise<number> => {
    if (!recipients.patients) return 0
    const res = await parseAxiosPromise({
        axiosPromise: httpGetPatients({
            params: {page: 1, limit: 1},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.patients.filters as any}
        }),
        responseShape: PatientPreviewResponse,
        onZodError: captureException
    })
    return res.meta.total
}

export const fetchConsultantsCount = async (recipients: SendMailPayload['recipients']): Promise<number> => {
    if (!recipients.consultants) return 0
    const res = await parseAxiosPromise({
        axiosPromise: httpGetUsers({
            params: {page: 1, limit: 1},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.consultants.filters as any}
        }),
        responseShape: HttpInfiniteUsersResponse,
        onZodError: captureException
    })
    return res.meta.total
}

export const fetchConsultantsPreview = async (
    recipients: SendMailPayload['recipients'],
    page: number
): Promise<PreviewPage> => {
    if (!recipients.consultants) return {data: [], meta: {total: 0, currentPage: 1, lastPage: 1}}
    const res = await parseAxiosPromise({
        axiosPromise: httpGetUsers({
            params: {page, limit: PAGE_LIMIT},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            payload: {filters: recipients.consultants.filters as any}
        }),
        responseShape: HttpInfiniteUsersResponse,
        onZodError: captureException
    })
    return {
        data: res.data.map(u => ({
            id: u.id,
            fullName: u.fullName,
            email: u.email
        })),
        meta: {
            total: res.meta.total,
            currentPage: res.meta.currentPage,
            lastPage: res.meta.lastPage
        }
    }
}
